import { isSameDay, startOfDay, parseISO } from 'date-fns';
import { TimeSlots as TSPickerTimeSlots } from '@ikea-customer-self-serve/ts-picker';
import { OutputFormData } from '@ikea-customer-self-serve/contact-form';
import { convertedTime } from './convertedTime';
import { ServiceLocation, timeWithoutOffset } from '../../helpers';

const { sortByPonyfill } = require('../../../ponyfill'); // eslint-disable-line

export interface Payload {
  appointmentId?: string;
  plannerId?: string;
  projectId?: string;
  customer: OutputFormData;
}

export interface AppointmentResponse {
  orderNo: string;
  payOrderLink?: string;
  questionsToken?: string;
  payOrderLinkEnabled: boolean;
}

export interface PayOrderLinkResponse {
  link: string;
}

type LocationType = 'STORE' | 'HOME' | 'STORE_REMOTE' | 'REMOTE' | 'STORE_HOME';

export interface Price {
  priceType?: string;
  priceInclTax?: number;
  priceExclTax?: number;
  currencyCode: string;
}

export interface AvailableServicesInZipResponse {
  serviceName: string;
  serviceProductId: string;
  price: Price;
  specialPrices: Price[];
  serviceItemNo: string;
  locationType: LocationType;
  serviceCode: string;
}

export interface AvailableTimeslotsResponse {
  id: string;
  from: string;
  to: string;
  externalId: string;
}

export interface TimeSlots extends TSPickerTimeSlots {
  id: string;
  appointmentId: string;
}

export interface Slots {
  date: string;
  timeSlots: TimeSlots[];
}

export interface TimeslotsResponse {
  timeSlots: Slots[];
}

export interface AvailableService {
  id: string;
  value: string;
  title: string;
  location: ServiceLocation;
  price: number;
  familyPrice: number;
  currencyCode: string;
  description: string;
  serviceCode: string;
}

export interface ServicesResponse {
  services: AvailableService[];
}

const getLocation = (locationType: LocationType): ServiceLocation => {
  switch (locationType) {
    case 'STORE':
      return 'inStore';
    case 'STORE_REMOTE':
      return 'storeRemote';
    case 'HOME':
      return 'home';
    case 'STORE_HOME':
      return 'storeHome';
    default:
      return 'remote';
  }
};

export const sortServicesByPrice = (
  serviceA: AvailableService,
  serviceB: AvailableService,
): number => {
  return serviceA.price - serviceB.price;
};

export const toAvailableService = (
  service: AvailableServicesInZipResponse,
): AvailableService => {
  const familyPrice = service.specialPrices.find(
    (specialPrice) => specialPrice.priceType === 'Family',
  ) || {
    priceInclTax: service.price.priceInclTax,
    priceExclTax: service.price.priceExclTax,
  };

  return {
    id: service.serviceProductId,
    value: service.serviceProductId,
    title: service.serviceProductId,
    location: getLocation(service.locationType),
    price: service.price.priceInclTax || service.price.priceExclTax || 0,
    familyPrice: familyPrice.priceInclTax || familyPrice.priceExclTax || 0,
    currencyCode: service.price.currencyCode,
    description: service.serviceProductId,
    serviceCode: service.serviceCode,
  };
};

export interface AvailableStore {
  value: string;
  text: string;
  street: string;
  zipCode: string;
  city: string;
}

export interface StoresResponse {
  stores: AvailableStore[];
}

export interface AvailableStoresResponse {
  name: string;
  street: string;
  zipCode: string;
  city: string;
  externalId: string;
}

export const toAvailableStore = (
  store: AvailableStoresResponse,
): AvailableStore => {
  return {
    value: store.externalId,
    text: store.name,
    street: store.street,
    zipCode: store.zipCode,
    city: store.city,
  };
};

export interface AvailableTimeSlot {
  id: string;
  from: string;
  to: string;
  timeSlotId: string;
}

const toTimeSlot = (
  from: string,
  to: string,
  id: string,
  externalId: string,
  isInStoreServices: boolean,
): TimeSlots => {
  return {
    from: convertedTime(from, isInStoreServices),
    to: convertedTime(to, isInStoreServices),
    dateFrom: from,
    dateTo: to,
    id,
    appointmentId: externalId,
  };
};

const toSlot = (date: string, timeSlots: TimeSlots[]): Slots => {
  return {
    date,
    timeSlots,
  };
};

const toOneSlotPerTimeRange = (slots: Slots[]) => {
  return slots.map((slot) => {
    const timeSlotsGroupedByTimeRange = slot.timeSlots.reduce(
      (map, timeSlot) => {
        const timeRange = `${timeSlot.dateFrom}-${timeSlot.dateTo}`;
        const groupedSlots = map.get(timeRange) || [];
        groupedSlots.push(timeSlot);
        return map.set(timeRange, groupedSlots);
      },
      new Map<string, TimeSlots[]>(),
    );

    const filteredTimeSlots = Array.from(
      timeSlotsGroupedByTimeRange.values(),
    ).map((allSlots) => {
      return allSlots[Math.floor(Math.random() * allSlots.length)];
    });

    return { ...slot, timeSlots: filteredTimeSlots };
  });
};

export const toSlots = async (
  slots: AvailableTimeslotsResponse[],
  isInStore: boolean,
): Promise<Slots[]> => {
  const slotArr: Slots[] = slots.reduce(
    (prevSlots: Slots[], slot: AvailableTimeslotsResponse): Slots[] => {
      const availableDate = timeWithoutOffset(slot.from);
      const existingSlotIndex = prevSlots.findIndex(
        (prevSlot: Slots): boolean => {
          if (isInStore) {
            return isSameDay(
              parseISO(prevSlot.date as string),
              parseISO(availableDate),
            );
          }

          return isSameDay(
            new Date(prevSlot.date as string),
            new Date(slot.from),
          );
        },
      );

      if (existingSlotIndex === -1) {
        const timeSlot: TimeSlots = toTimeSlot(
          slot.from,
          slot.to,
          slot.id,
          slot.externalId,
          isInStore,
        );

        const timeSlotDay = isInStore
          ? availableDate
          : startOfDay(parseISO(slot.from)).toISOString();

        prevSlots.push(toSlot(timeSlotDay, [timeSlot]));
        return prevSlots;
      }

      return prevSlots.map((prevSlot, index): Slots => {
        if (index === existingSlotIndex) {
          return toSlot(prevSlot.date, [
            ...prevSlot.timeSlots,
            toTimeSlot(slot.from, slot.to, slot.id, slot.externalId, isInStore),
          ]);
        }

        return prevSlot;
      });
    },
    [],
  );

  await Promise.all(
    slotArr.map(
      (slot: Slots): Promise<Slots> =>
        new Promise((resolve): void => {
          const sortedSlot = sortByPonyfill(
            slot.timeSlots,
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            (timeSlot: TimeSlots) => [timeSlot.dateFrom, timeSlot.dateTo],
          );
          resolve(sortedSlot);
        }),
    ),
  );

  const oneSlotPerTimeRange = toOneSlotPerTimeRange(slotArr);
  oneSlotPerTimeRange.sort((a: Slots, b: Slots): number => {
    const aDate = a.date;
    const bDate = b.date;
    if (aDate && bDate && aDate > bDate) {
      return 1;
    }
    if (aDate && bDate && aDate < bDate) {
      return -1;
    }
    return 0;
  });

  return oneSlotPerTimeRange;
};
